import type { Integration } from '../types/integrations';

export interface GuideProviderContext {
  name: string;
  displayName: string;
  documentationUrl?: string;
}

export interface GuideContext {
  integration?: Integration;
  provider: GuideProviderContext;
}

const MISSING_GENERIC = '[not available]';
const MISSING_SECRET = '[rotate secret to reveal]';

type Resolver = (ctx: GuideContext) => string | undefined;

const resolvers: Record<string, { resolve: Resolver; missing: string }> = {
  'integration.webhookUrl': {
    resolve: (ctx) => ctx.integration?.webhookUrl,
    missing: MISSING_GENERIC,
  },
  'integration.webhookSecret': {
    resolve: (ctx) => ctx.integration?.webhookSecret,
    missing: MISSING_SECRET,
  },
  'integration.webhookId': {
    resolve: (ctx) => ctx.integration?.webhookId,
    missing: MISSING_GENERIC,
  },
  'integration.id': {
    resolve: (ctx) => ctx.integration?.integrationId,
    missing: MISSING_GENERIC,
  },
  'integration.displayName': {
    resolve: (ctx) => ctx.integration?.displayName,
    missing: MISSING_GENERIC,
  },
  'integration.ingestionType': {
    resolve: (ctx) => ctx.integration?.ingestionType,
    missing: MISSING_GENERIC,
  },
  'provider.name': {
    resolve: (ctx) => ctx.provider.name,
    missing: MISSING_GENERIC,
  },
  'provider.displayName': {
    resolve: (ctx) => ctx.provider.displayName,
    missing: MISSING_GENERIC,
  },
  'provider.documentationUrl': {
    resolve: (ctx) => ctx.provider.documentationUrl,
    missing: MISSING_GENERIC,
  },
};

const TOKEN_REGEX = /\{\{\s*([\w.]+)\s*\}\}/g;

export function applyGuideTokens(markdown: string, ctx: GuideContext): string {
  return markdown.replace(TOKEN_REGEX, (match, path: string) => {
    const entry = resolvers[path];
    if (!entry) return match;
    const value = entry.resolve(ctx);
    return value && value.length > 0 ? value : entry.missing;
  });
}

export function getResolvedTokenValues(ctx: GuideContext): string[] {
  const values: string[] = [];
  for (const entry of Object.values(resolvers)) {
    const v = entry.resolve(ctx);
    if (v && v.length > 0) values.push(v);
  }
  return values;
}

const COPY_MARKER_RE = /`([^`\n]+)`\*/g;

/**
 * Extract `value`* copy markers from guide markdown. Each marked inline-code
 * span contributes its inner text to `values`, and the `*` is stripped from
 * the returned markdown so the downstream renderer sees plain inline code.
 *
 * Only matches inline code (single backticks, single line). Fenced blocks
 * already have their own copy button via CodeBlock.
 */
export function extractCopyMarkers(markdown: string): {
  markdown: string;
  values: string[];
} {
  const values: string[] = [];
  const stripped = markdown.replace(COPY_MARKER_RE, (_m, text: string) => {
    values.push(text);
    return '`' + text + '`';
  });
  return { markdown: stripped, values };
}
